import { useState } from 'react'
import { Download, Copy, Check, FileText, Calendar } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useToast } from '@/components/ui/use-toast'
import type { BoletoPayment as BoletoPaymentType } from '@/types/payment'
import { formatCurrency } from '@/lib/utils'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

interface BoletoPaymentProps {
  boletoData: BoletoPaymentType
}

export function BoletoPayment({ boletoData }: BoletoPaymentProps) {
  const [copiedBarcode, setCopiedBarcode] = useState(false)
  const [copiedLine, setCopiedLine] = useState(false)
  const { toast } = useToast()

  const handleCopyBarcode = async () => {
    try {
      await navigator.clipboard.writeText(boletoData.boleto.barcode)
      setCopiedBarcode(true)
      toast({
        title: 'Código de barras copiado!',
        description: 'Cole no aplicativo do seu banco',
      })
      setTimeout(() => setCopiedBarcode(false), 3000)
    } catch (err) {
      toast({
        title: 'Erro ao copiar',
        description: 'Tente novamente',
        variant: 'destructive',
      })
    }
  }

  const handleCopyLine = async () => {
    try {
      await navigator.clipboard.writeText(boletoData.boleto.digitable_line)
      setCopiedLine(true)
      toast({
        title: 'Linha digitável copiada!',
        description: 'Cole no aplicativo do seu banco',
      })
      setTimeout(() => setCopiedLine(false), 3000)
    } catch (err) {
      toast({
        title: 'Erro ao copiar',
        description: 'Tente novamente',
        variant: 'destructive',
      })
    }
  }

  const handleDownloadPdf = () => {
    window.open(boletoData.boleto.pdf_url, '_blank')
  }

  const dueDate = new Date(boletoData.boleto.due_date)

  return (
    <Card>
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center gap-2">
          <FileText className="h-6 w-6" />
          Boleto Bancário
        </CardTitle>
        <CardDescription>
          Pague em qualquer banco, lotérica ou aplicativo
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Valor e Vencimento */}
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <p className="text-sm text-muted-foreground">Valor</p>
            <p className="text-2xl font-bold text-primary">
              {formatCurrency(boletoData.amount)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Vencimento</p>
            <div className="flex items-center justify-center gap-1">
              <Calendar className="h-4 w-4" />
              <p className="text-lg font-semibold">
                {format(dueDate, 'dd/MM/yyyy', { locale: ptBR })}
              </p>
            </div>
          </div>
        </div>

        <Separator />

        {/* Botão Download PDF */}
        <Button
          onClick={handleDownloadPdf}
          className="w-full"
          size="lg"
        >
          <Download className="mr-2 h-5 w-5" />
          Download do Boleto (PDF)
        </Button>

        {/* Linha Digitável */}
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Linha digitável:
          </label>
          <div className="flex gap-2">
            <div className="flex-1 rounded-md border bg-muted px-3 py-3">
              <p className="text-center text-sm font-mono break-all">
                {boletoData.boleto.digitable_line}
              </p>
            </div>
            <Button
              onClick={handleCopyLine}
              variant="outline"
              size="icon"
              className="shrink-0"
            >
              {copiedLine ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Código de Barras */}
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Código de barras:
          </label>
          <div className="flex gap-2">
            <div className="flex-1 rounded-md border bg-muted px-3 py-2">
              <p className="truncate text-xs font-mono">
                {boletoData.boleto.barcode}
              </p>
            </div>
            <Button
              onClick={handleCopyBarcode}
              variant="outline"
              size="icon"
              className="shrink-0"
            >
              {copiedBarcode ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Instruções */}
        <div className="space-y-2 rounded-lg bg-muted p-4">
          <h4 className="font-semibold text-sm">Como pagar:</h4>
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li>Faça o download do PDF do boleto</li>
            <li>Pague em qualquer banco, lotérica ou app bancário</li>
            <li>Ou use a linha digitável para pagamento online</li>
            <li>O pagamento pode levar até 3 dias úteis para confirmar</li>
          </ol>
        </div>

        {/* Aviso de Vencimento */}
        <div className="rounded-lg bg-yellow-100 p-3 text-sm text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
          <strong>Atenção:</strong> Pague até{' '}
          {format(dueDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })} para evitar juros.
        </div>

        {/* Status */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-2 text-sm text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
            <div className="h-2 w-2 animate-pulse rounded-full bg-blue-600" />
            Aguardando pagamento
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
